const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/dashboard',
};

// Live marketing/legal site (apps/website) deployed at this origin.
const ARTHAM_WEB = 'https://arthampay.app';

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  // Live legal/support pages on the marketing site (open in the system browser).
  website: {
    terms: `${ARTHAM_WEB}/terms`,
    privacy: `${ARTHAM_WEB}/privacy`,
    grievance: `${ARTHAM_WEB}/grievance`,
    faqs: `${ARTHAM_WEB}/contact`, // website FAQ lives in the Contact page
  },
  supportEmail: 'support@arthampay.app',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  components: '/components',
  docs: 'https://docs.minimals.cc/',
  changelog: 'https://docs.minimals.cc/changelog/',
  zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figmaUrl: 'https://www.figma.com/design/WadcoP3CSejUDj7YZc87xj/%5BPreview%5D-Minimal-Web.v7.3.0',
  // AUTH
  auth: {
    amplify: {
      signIn: `${ROOTS.AUTH}/amplify/sign-in`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      signUp: `${ROOTS.AUTH}/amplify/sign-up`,
      updatePassword: `${ROOTS.AUTH}/amplify/update-password`,
      resetPassword: `${ROOTS.AUTH}/amplify/reset-password`,
    },
    jwt: {
      signIn: `${ROOTS.AUTH}/jwt/sign-in`,
      signUp: `${ROOTS.AUTH}/jwt/sign-up`,
    },
    firebase: {
      signIn: `${ROOTS.AUTH}/firebase/sign-in`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      signUp: `${ROOTS.AUTH}/firebase/sign-up`,
      resetPassword: `${ROOTS.AUTH}/firebase/reset-password`,
      phoneSignIn: `${ROOTS.AUTH}/firebase/phone-sign-in`,
    },
    auth0: { signIn: `${ROOTS.AUTH}/auth0/sign-in` },
    supabase: {
      signIn: `${ROOTS.AUTH}/supabase/sign-in`,
      verify: `${ROOTS.AUTH}/supabase/verify`,
      signUp: `${ROOTS.AUTH}/supabase/sign-up`,
      updatePassword: `${ROOTS.AUTH}/supabase/update-password`,
      resetPassword: `${ROOTS.AUTH}/supabase/reset-password`,
    },
  },
  authDemo: {
    split: {
      signIn: `${ROOTS.AUTH_DEMO}/split/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/split/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/split/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/split/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/split/verify`,
    },
    centered: {
      signIn: `${ROOTS.AUTH_DEMO}/centered/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/centered/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/centered/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/centered/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/centered/verify`,
    },
  },
  // DASHBOARD
  //
  // URL convention: real IDs are passed via query params, never path segments.
  // Capacitor ships as a static export; `/foo/[id]/bar` would require every id at build time.
  // Pattern: `/foo/bar?id=<id>`. If the id is absent, the page should redirect to the list.
  dashboard: {
    root: ROOTS.DASHBOARD,
    artham: {
      send: `${ROOTS.DASHBOARD}/send-money`,
      senders: `${ROOTS.DASHBOARD}/senders`,
      beneficiary: (id: string) =>
        `${ROOTS.DASHBOARD}/beneficiaries/detail?id=${encodeURIComponent(id)}`,
      history: `${ROOTS.DASHBOARD}/history`,
      networkActivity: `${ROOTS.DASHBOARD}/network-activity`,
      earnings: `${ROOTS.DASHBOARD}/earnings`,
      credit: `${ROOTS.DASHBOARD}/credit`,
      creditRequest: `${ROOTS.DASHBOARD}/credit/request`,
      profile: `${ROOTS.DASHBOARD}/profile`,
      users: `${ROOTS.DASHBOARD}/user`,
      // Admin/Reviewer "Users" directory + full-page user profile ("Palantir" view).
      usersDirectory: `${ROOTS.DASHBOARD}/users`,
      userProfile: (uid: string) =>
        `${ROOTS.DASHBOARD}/users/detail?uid=${encodeURIComponent(uid)}`,
      dealers: `${ROOTS.DASHBOARD}/dealers`,
      distributors: `${ROOTS.DASHBOARD}/distributors`,
      collection: `${ROOTS.DASHBOARD}/collection`,
      manageDealers: `${ROOTS.DASHBOARD}/dealers`,
      inviteDealer: `${ROOTS.DASHBOARD}/manage-dealers/invite`,
      bulkInviteDealers: `${ROOTS.DASHBOARD}/dealers/bulk-invite`,
      creditRequestQueue: `${ROOTS.DASHBOARD}/credit/queue`,
      dealerDetail: (id: string) =>
        `${ROOTS.DASHBOARD}/dealers/detail?id=${encodeURIComponent(id)}`,
      depositToAdmin: `${ROOTS.DASHBOARD}/deposit`,
      privacy: `${ROOTS.DASHBOARD}/privacy`,
      // Admin NEW-v1 sections (B5)
      settlement: {
        daily: `${ROOTS.DASHBOARD}/settlement`,
        weekly: `${ROOTS.DASHBOARD}/settlement/weekly`,
      },
      reconciliation: `${ROOTS.DASHBOARD}/reconciliation`,
      variance: `${ROOTS.DASHBOARD}/variance`,
      float: `${ROOTS.DASHBOARD}/float`,
      chargebacks: `${ROOTS.DASHBOARD}/chargebacks`,
      ota: `${ROOTS.DASHBOARD}/ota`,
      reports: {
        gmv: `${ROOTS.DASHBOARD}/reports/gmv`,
        commissionPaid: `${ROOTS.DASHBOARD}/reports/commission-paid`,
        disputeVolume: `${ROOTS.DASHBOARD}/reports/dispute-volume`,
        settlement: `${ROOTS.DASHBOARD}/reports/settlement`,
      },
      exports: `${ROOTS.DASHBOARD}/exports`,
      commissions: {
        dealerDetail: (id: string) =>
          `${ROOTS.DASHBOARD}/commissions/dealers/detail?id=${encodeURIComponent(id)}`,
        tiers: `${ROOTS.DASHBOARD}/commissions/tiers`,
        tds: `${ROOTS.DASHBOARD}/commissions/tds`,
        payouts: {
          queue: `${ROOTS.DASHBOARD}/commissions/payouts`,
          detail: (id: string) =>
            `${ROOTS.DASHBOARD}/commissions/payouts/detail?id=${encodeURIComponent(id)}`,
        },
      },
      grievances: {
        root: `${ROOTS.DASHBOARD}/grievances`,
        new: `${ROOTS.DASHBOARD}/grievances/new`,
        details: (id: string) =>
          `${ROOTS.DASHBOARD}/grievances/detail?id=${encodeURIComponent(id)}`,
        onBehalf: (dealerId: string) =>
          `${ROOTS.DASHBOARD}/dealers/grievance?id=${encodeURIComponent(dealerId)}`,
      },
      kyc: {
        root: `${ROOTS.DASHBOARD}/kyc`,
        resubmit: `${ROOTS.DASHBOARD}/kyc/resubmit`,
      },
      reviewer: {
        root: `${ROOTS.DASHBOARD}/reviewer`,
        dashboard: `${ROOTS.DASHBOARD}/reviewer`,
        grievances: `${ROOTS.DASHBOARD}/reviewer/grievances`,
        grievance: (id: string) =>
          `${ROOTS.DASHBOARD}/reviewer/grievances/detail?id=${encodeURIComponent(id)}`,
        kyc: `${ROOTS.DASHBOARD}/reviewer/kyc`,
        kycReview: (uid: string) =>
          `${ROOTS.DASHBOARD}/reviewer/kyc/detail?uid=${encodeURIComponent(uid)}`,
        notifications: `${ROOTS.DASHBOARD}/reviewer/notifications`,
        auditLog: `${ROOTS.DASHBOARD}/reviewer/audit-log`,
        profile: `${ROOTS.DASHBOARD}/reviewer/profile`,
      },
      admin: {
        auditLog: `${ROOTS.DASHBOARD}/audit-log`,
        errorLog: `${ROOTS.DASHBOARD}/error-log`,
        ekoLog: `${ROOTS.DASHBOARD}/eko-log`,
        ledger: `${ROOTS.DASHBOARD}/ledger`,
        ledgerEntries: `${ROOTS.DASHBOARD}/ledger/entries`,
        disputes: `${ROOTS.DASHBOARD}/disputes`,
        disputeDetail: (id: string) =>
          `${ROOTS.DASHBOARD}/disputes/detail?id=${encodeURIComponent(id)}`,
        promos: `${ROOTS.DASHBOARD}/commissions/promos`,
        providerHealth: `${ROOTS.DASHBOARD}/provider-health`,
        geofence: `${ROOTS.DASHBOARD}/geofence`,
        notificationsTemplates: `${ROOTS.DASHBOARD}/notifications/templates`,
        notificationsTestSend: `${ROOTS.DASHBOARD}/notifications/test-send`,
        txnFailed: `${ROOTS.DASHBOARD}/transactions/failed`,
        txnStuck: `${ROOTS.DASHBOARD}/transactions/stuck`,
        txnApprovals: `${ROOTS.DASHBOARD}/transactions/approvals`,
        txnDetail: (id: string) =>
          `${ROOTS.DASHBOARD}/transactions/detail?id=${encodeURIComponent(id)}`,
      },
    },
    dealer: {
      root: `${ROOTS.DASHBOARD}/dealers`,
      new: `${ROOTS.DASHBOARD}/dealers/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/dealers/edit?id=${encodeURIComponent(id)}`,
    },
    distributor: {
      root: `${ROOTS.DASHBOARD}/distributors`,
      new: `${ROOTS.DASHBOARD}/distributors/new`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/distributors/edit?id=${encodeURIComponent(id)}`,
    },
    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      account: `${ROOTS.DASHBOARD}/user/account`,
    },
  },
};
